import { useCallback } from 'react';

import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import {
  getOtherTaskTemplate,
  getScanWithUpdatedGroup,
  getScopeForScanPartChildren,
  initTaskFromTemplate,
  isOtherTask,
  type ScanGroupOptimized,
  type ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { isScopeNavigatorTask } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { addFreshTaskToScope, removeTaskFromScope } from '@/Client/Lib/taskScopeLifecycle';

/**
 * Params are what the *page* knows — the route's group, the sorted groups, and
 * the navigation callbacks. Scan data and its mutators come from
 * ScanEditingContext directly rather than being threaded through.
 */
type AdminScanPageScopeSidebarActionsParams = {
  sortedScanGroups: readonly ScanGroupOptimized[];
  group?: ScanGroupOptimized;
  focusPanelNavigationActive: boolean;
  navigateWithinFocusPanel: (params: { groupId?: string; contractorId?: string; taskId?: string; expandTask?: boolean; replace?: boolean }) => void;
  setCurrentGroupIdAndTaskId: (groupId: string | undefined, taskId: string | undefined) => void;
};

/**
 * The label a duplicate wears, mirroring `cloneGroup`: walk the siblings that came from the same
 * template and take the next free number. A standard task carries it as its `customLabel`, which
 * the title presenter renders alongside the template name — "Cabinets (2)". A custom task's
 * `customLabel` *is* its name, so the number is appended to that instead of replacing it.
 */
export const getDuplicateTaskLabel = (group: ScanGroupOptimized, sourceTask: ScanTaskOptimized): string => {
  const siblings = (group.tasks ?? []).filter(task => task.templateId === sourceTask.templateId);
  const customName = isOtherTask(sourceTask) ? sourceTask.customLabel?.replace(/\s*\d+$/, '').trim() : undefined;
  const nextNumber = siblings.reduce((highest, task) => {
    const [numberText] = task.customLabel?.match(/\d+$/) ?? [];
    const used = numberText ? parseInt(numberText, 10) : 1;
    return used >= highest ? used + 1 : highest;
  }, 2);

  return customName ? `${customName} ${nextNumber}` : nextNumber.toString();
};

export const useAdminScanPageScopeSidebarActions = ({
  sortedScanGroups,
  group,
  focusPanelNavigationActive,
  navigateWithinFocusPanel,
  setCurrentGroupIdAndTaskId,
}: AdminScanPageScopeSidebarActionsParams) => {
  const { scanRoot, scanDataMutators } = useScanEditingContextSlice(['scanRoot', 'scanDataMutators']);

  const selectCloseoutSidebarGroup = useCallback(
    (groupId: string) => {
      if (focusPanelNavigationActive) {
        navigateWithinFocusPanel({ groupId });
        return;
      }
      const nextGroup = sortedScanGroups.find(candidateGroup => candidateGroup.id === groupId);
      const firstTaskId = nextGroup?.tasks?.filter(isScopeNavigatorTask)[0]?.id;
      setCurrentGroupIdAndTaskId(groupId, firstTaskId);
    },
    [focusPanelNavigationActive, navigateWithinFocusPanel, setCurrentGroupIdAndTaskId, sortedScanGroups],
  );

  const selectCloseoutSidebarTask = useCallback(
    (taskId: string) => {
      const nextGroup = sortedScanGroups.find(candidateGroup => candidateGroup.tasks?.some(candidateTask => candidateTask.id === taskId));
      if (focusPanelNavigationActive && nextGroup) {
        navigateWithinFocusPanel({ groupId: nextGroup.id, taskId });
        return;
      }
      setCurrentGroupIdAndTaskId(nextGroup?.id ?? group?.id, taskId);
    },
    [focusPanelNavigationActive, group?.id, navigateWithinFocusPanel, setCurrentGroupIdAndTaskId, sortedScanGroups],
  );

  const addTask = useCallback(
    async (groupId: string, taskId: string) => {
      const newTaskId = await addFreshTaskToScope(scanDataMutators, groupId, taskId);
      trackEvent(ANALYTICS_EVENTS.scanTaskAdded, {
        scanId: scanRoot?.id,
        groupId,
        taskId: newTaskId,
        source: 'template',
      });
      if (focusPanelNavigationActive) {
        navigateWithinFocusPanel({ groupId, taskId: newTaskId, expandTask: true });
      } else {
        setCurrentGroupIdAndTaskId(groupId, newTaskId);
      }
    },
    [focusPanelNavigationActive, navigateWithinFocusPanel, scanDataMutators, scanRoot?.id, setCurrentGroupIdAndTaskId],
  );

  const addCustomTask = useCallback(
    async (groupId: string, customLabel: string | undefined) => {
      if (!scanRoot) return;
      const scanGroup = scanRoot.groups?.find(item => item.id === groupId);
      const otherTaskTemplate = scanGroup ? getOtherTaskTemplate(scanRoot, scanGroup) : undefined;
      if (!scanGroup || !otherTaskTemplate) return;

      const label = customLabel?.trim();
      const newTask = initTaskFromTemplate(
        otherTaskTemplate,
        getScopeForScanPartChildren(scanGroup),
        Date.now(),
      );
      const taskToAdd: ScanTaskOptimized = {
        ...newTask,
        userAdded: true,
        ...(label ? { customLabel: label } : {}),
      };

      await scanDataMutators.updateScan(
        getScanWithUpdatedGroup(scanRoot, groupId, {
          tasks: [...(scanGroup.tasks ?? []), taskToAdd],
        }),
      );
      trackEvent(ANALYTICS_EVENTS.scanTaskAdded, {
        scanId: scanRoot.id,
        groupId,
        taskId: taskToAdd.id,
        source: 'custom',
      });

      if (focusPanelNavigationActive) {
        navigateWithinFocusPanel({ groupId, taskId: taskToAdd.id, expandTask: true });
      } else {
        setCurrentGroupIdAndTaskId(groupId, taskToAdd.id);
      }
    },
    [focusPanelNavigationActive, navigateWithinFocusPanel, scanDataMutators, scanRoot, setCurrentGroupIdAndTaskId],
  );

  const canAddCustomTaskForGroup = useCallback(
    (scanGroup: ScanGroupOptimized) => !!scanRoot && !!getOtherTaskTemplate(scanRoot, scanGroup),
    [scanRoot],
  );

  const cloneGroup = useCallback(
    async (groupId: string) => {
      await scanDataMutators.cloneGroup(groupId);
      trackEvent(ANALYTICS_EVENTS.scanGroupCloned, { scanId: scanRoot?.id, groupId });
    },
    [scanDataMutators, scanRoot?.id],
  );

  const deleteGroup = useCallback(
    async (groupId: string) => {
      await scanDataMutators.deleteGroup(groupId);
      trackEvent(ANALYTICS_EVENTS.scanGroupDeleted, { scanId: scanRoot?.id, groupId });
    },
    [scanDataMutators, scanRoot?.id],
  );

  /**
   * Another task of the same kind, sitting right after the one it came from — the same thing
   * `cloneGroup` means by "clone", and for the same reason: a task is a slot, and needing two of
   * them is how you scope two runs of the same work. It is deliberately *not* a copy of the
   * source's selections. Options and line items are keyed by id (`optionsById`, `lineItemsById`),
   * so copying them would need every nested id re-minted, and carrying a task's approvals and
   * statuses into a brand-new one is its own decision rather than a detail of this one.
   */
  const duplicateTask = useCallback(
    async (taskId: string) => {
      if (!scanRoot) return;
      const taskGroup = scanRoot.groups?.find(
        candidateGroup => candidateGroup.tasks?.some(candidateTask => candidateTask.id === taskId),
      );
      const sourceTask = taskGroup?.tasksById[taskId];
      if (!taskGroup || !sourceTask?.template) return;

      const duplicate: ScanTaskOptimized = {
        ...initTaskFromTemplate(sourceTask.template, getScopeForScanPartChildren(taskGroup), Date.now()),
        userAdded: true,
        customLabel: getDuplicateTaskLabel(taskGroup, sourceTask),
      };

      await scanDataMutators.updateScan(
        getScanWithUpdatedGroup(scanRoot, taskGroup.id, {
          tasks: (taskGroup.tasks ?? []).flatMap(task => task.id === taskId ? [task, duplicate] : [task]),
        }),
      );
      trackEvent(ANALYTICS_EVENTS.scanTaskAdded, {
        scanId: scanRoot.id,
        groupId: taskGroup.id,
        taskId: duplicate.id,
        source: 'duplicate',
      });

      if (focusPanelNavigationActive) {
        navigateWithinFocusPanel({ groupId: taskGroup.id, taskId: duplicate.id, expandTask: true });
      } else {
        setCurrentGroupIdAndTaskId(taskGroup.id, duplicate.id);
      }
    },
    [focusPanelNavigationActive, navigateWithinFocusPanel, scanDataMutators, scanRoot, setCurrentGroupIdAndTaskId],
  );

  const deleteTask = useCallback(
    async (taskId: string) => {
      const taskGroup = scanRoot?.groups?.find(
        candidateGroup => candidateGroup.tasks?.some(candidateTask => candidateTask.id === taskId),
      );
      if (!taskGroup) return;
      await removeTaskFromScope(scanDataMutators, taskGroup.id, taskId);
      trackEvent(ANALYTICS_EVENTS.scanTaskDeleted, { scanId: scanRoot?.id, groupId: taskGroup.id, taskId });
      /* The task is only soft-removed, so the route still resolves it and the editor would sit on
         a task that is no longer in scope. Fall back to the group it came from, the way the
         focused editor's own delete did before this moved to the navigator. */
      if (focusPanelNavigationActive) {
        navigateWithinFocusPanel({ groupId: taskGroup.id, taskId: undefined, replace: true });
      } else {
        setCurrentGroupIdAndTaskId(taskGroup.id, undefined);
      }
    },
    [focusPanelNavigationActive, navigateWithinFocusPanel, scanDataMutators, scanRoot, setCurrentGroupIdAndTaskId],
  );

  return {
    selectCloseoutSidebarGroup,
    selectCloseoutSidebarTask,
    addTask,
    addCustomTask,
    canAddCustomTaskForGroup,
    cloneGroup,
    deleteGroup,
    deleteTask,
    duplicateTask,
  };
};
